import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Image as ImgIcon, X, Lock, Loader2 } from "lucide-react";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { api, errorMessage } from "../lib/api";

const MAX_IMAGES = 3;
const MAX_BYTES = 2 * 1024 * 1024;

function StarPicker({ value, onChange, size = 28 }) {
  return (
    <div className="flex gap-1" data-testid="star-picker">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="transition-transform hover:scale-110"
          data-testid={`star-${n}`}
          aria-label={`${n} stars`}
        >
          <Star
            style={{ width: size, height: size }}
            className={n <= value ? "fill-[#FF1F87] text-[#FF1F87]" : "text-gray-300"}
          />
        </button>
      ))}
    </div>
  );
}

function ReviewCard({ r }) {
  const initials = (r.full_name || "").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="bg-white rounded-3xl p-5 shadow-sm" data-testid={`review-card-${r.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full ss-gradient text-white flex items-center justify-center font-extrabold text-sm flex-shrink-0">{initials || "??"}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display font-extrabold">{r.full_name}</p>
            <StarPicker value={r.rating} onChange={() => {}} size={14} />
          </div>
          <p className="text-[10px] uppercase tracking-widest text-gray-400 mt-0.5">{new Date(r.created_at).toLocaleDateString()}</p>
          <p className="text-sm text-[#374151] mt-2 whitespace-pre-wrap break-words">{r.text}</p>
          {r.images?.length > 0 && (
            <div className="flex gap-2 mt-3">
              {r.images.map((src, i) => (
                <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="block">
                  <img src={src} alt={`review ${i + 1}`} className="w-20 h-20 object-cover rounded-xl" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReviewsPage() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [canReview, setCanReview] = useState(false);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [images, setImages] = useState([]); // data URLs
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/reviews").then(r => setReviews(r.data || [])).catch(() => {});
    if (user && user.role !== "admin") {
      api.get("/reviews/me/can-review").then(r => setCanReview(!!r.data.can_review)).catch(() => {});
    }
  }, [user]);

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (images.length + files.length > MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images`); return;
    }
    files.forEach(f => {
      if (f.size > MAX_BYTES) { toast.error(`${f.name} is over 2MB`); return; }
      const reader = new FileReader();
      reader.onload = () => setImages(prev => [...prev, reader.result]);
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const removeImage = (i) => setImages(imgs => imgs.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 4) { toast.error("Write a few words"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews", { rating, text: text.trim(), images });
      setReviews(rs => [data, ...rs]);
      setText(""); setImages([]); setRating(5);
      toast.success("Thanks for your review! 🍪");
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setSubmitting(false); }
  };

  const avg = reviews.length ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length) : 0;

  return (
    <Layout>
      <section className="px-4 sm:px-6 py-10 sm:py-14 max-w-3xl mx-auto" data-testid="reviews-page">
        <div className="text-center mb-8">
          <h1 className="font-display font-extrabold text-3xl sm:text-5xl text-white drop-shadow-lg">Reviews</h1>
          {reviews.length > 0 && (
            <div className="mt-3 inline-flex items-center gap-2 bg-white/90 backdrop-blur px-4 py-1.5 rounded-full">
              <Star className="w-4 h-4 fill-[#FF1F87] text-[#FF1F87]" />
              <span className="font-display font-extrabold text-[#FF1F87]">{avg.toFixed(1)}</span>
              <span className="text-xs text-[#4B5563]">· {reviews.length} review{reviews.length === 1 ? "" : "s"}</span>
            </div>
          )}
        </div>

        {/* Write a review */}
        {!user ? (
          <div className="bg-white rounded-3xl p-6 mb-6 text-center">
            <Lock className="w-8 h-8 text-[#FF1F87] mx-auto mb-2" />
            <p className="text-sm text-[#4B5563]"><Link to="/login" className="font-bold text-[#FF1F87] underline">Sign in</Link> to leave a review after your first order.</p>
          </div>
        ) : !canReview ? (
          user.role !== "admin" && (
            <div className="bg-white rounded-3xl p-6 mb-6 text-center" data-testid="review-locked">
              <Lock className="w-8 h-8 text-[#FF1F87] mx-auto mb-2" />
              <p className="text-sm text-[#4B5563]">Reviews are reserved for customers who've ordered.<br />Place your first box and come back here to share the love.</p>
              <Link to="/order" className="btn-pink mt-4 inline-block">ORDER COOKIES</Link>
            </div>
          )
        ) : (
          <form onSubmit={submit} className="bg-white rounded-3xl p-6 mb-6 space-y-4" data-testid="review-form">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-xs uppercase tracking-widest text-[#FF1F87] font-bold">Your Rating</p>
              <StarPicker value={rating} onChange={setRating} />
            </div>
            <textarea
              className="input-base !py-3 min-h-[120px]"
              maxLength={1500}
              placeholder="Tell other customers how the cookies were…"
              value={text}
              onChange={e => setText(e.target.value)}
              data-testid="review-text"
            />
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-2">Add Photos (optional, up to {MAX_IMAGES})</p>
              <div className="flex gap-2 flex-wrap">
                {images.map((img, i) => (
                  <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-gray-200">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5" aria-label="remove">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {images.length < MAX_IMAGES && (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="w-20 h-20 rounded-xl border-2 border-dashed border-[#FF5A7E] flex items-center justify-center text-[#FF1F87]"
                    data-testid="review-add-image"
                  >
                    <ImgIcon className="w-6 h-6" />
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPickFiles} />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting || text.trim().length < 4}
              className="btn-pink w-full disabled:opacity-50"
              data-testid="review-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "POST REVIEW"}
            </button>
          </form>
        )}

        {/* List */}
        <div className="space-y-3" data-testid="reviews-list">
          {reviews.length === 0 ? (
            <div className="bg-white rounded-3xl p-8 text-center">
              <Star className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-[#4B5563]">No reviews yet. Be the first to share!</p>
            </div>
          ) : (
            reviews.map(r => <ReviewCard key={r.id} r={r} />)
          )}
        </div>
      </section>
    </Layout>
  );
}
